/**
 * Pharmacist line pricing (Rx-03/Rx-04) and the live total preview.
 *
 * The preview mirrors backend services/gst_calculator.py + pricing_service.py exactly, so the
 * total updates as the pharmacist types with no round trip:
 *   taxable = qty × MRP (paise, exact)
 *   GST     = within the pharmacy's state: CGST + SGST, each taxable × rate/2 rounded on its own;
 *             otherwise IGST, taxable × rate rounded once — rounding half-to-even to the paisa,
 *             like Python's Decimal ROUND_HALF_EVEN
 *   total   = Σ taxable + delivery + Σ line GST + GST on delivery (pharmacy's delivery rate)
 * Money is BigInt paise throughout (floating point can't represent ₹0.10 exactly). The server
 * stays the authority: publishing sends the total shown, and the server refuses to publish if
 * its own recomputation differs (PRICE_MISMATCH).
 */

import type { OrderLine, PricingBasis } from "../api/orders";
import { isBlank } from "./form";

/** What the pharmacist has typed for one line, before it is published. */
export interface LineDraft {
  qty: string;
  mrp: string;
  remark: string;
}

// NUMERIC(12,2): up to 10 whole digits and 2 decimals; the largest storable amount.
const MONEY = /^\d{1,10}(\.\d{1,2})?$/;
export const MAX_PAISE = 999_999_999_999n;

/** Exact paise for a typed amount ("185.9" → 18590n), or null when it isn't valid money. */
export function toPaise(input: string): bigint | null {
  const s = input.trim();
  if (!MONEY.test(s)) return null;
  const [whole, frac = ""] = s.split(".");
  return BigInt(whole) * 100n + BigInt(frac.padEnd(2, "0"));
}

/** Paise as the API's decimal string (18590n → "185.90"). */
export function paiseToString(paise: bigint): string {
  return `${paise / 100n}.${(paise % 100n).toString().padStart(2, "0")}`;
}

/** num / den rounded half-to-even (both non-negative), as Decimal.quantize(ROUND_HALF_EVEN). */
function divHalfEven(num: bigint, den: bigint): bigint {
  const q = num / den;
  const twice = (num % den) * 2n;
  return twice > den || (twice === den && q % 2n === 1n) ? q + 1n : q;
}

/** A GST rate from the API ("12.00", "0.25") in hundredths of a percent; null if malformed. */
function rateHundredths(pct: string): bigint | null {
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(pct.trim());
  return m ? BigInt(m[1]) * 100n + BigInt((m[2] ?? "").padEnd(2, "0")) : null;
}

/** GST in paise on a taxable amount, exactly as gst_calculator.compute_gst. */
export function gstPaise(taxable: bigint, pct: string, intraState: boolean): bigint | null {
  const rate = rateHundredths(pct);
  if (rate === null) return null;
  // amount = taxable × (rate/100)% → taxable × rate / 10 000 (per half: / 20 000)
  return intraState ? 2n * divHalfEven(taxable * rate, 20_000n) : divHalfEven(taxable * rate, 10_000n);
}

/** An unpriced line starts at the full prescribed quantity and the MRP seeded from the catalogue. */
export const draftFor = (line: OrderLine): LineDraft => ({
  qty: String(line.quantity_dispensed || line.quantity_prescribed),
  mrp: line.mrp_per_unit,
  remark: line.quantity_remark ?? "",
});

/** Why the draft can't be priced, or null when it can. An available line needs at least 1 unit
 *  and an MRP above zero, as the API's publish check does (LINES_INCOMPLETE); a medicine that
 *  can't be supplied is marked not available instead (PHA-04). */
export function lineProblem(line: OrderLine, draft: LineDraft): string | null {
  const qty = Number(draft.qty);
  if (!/^\d+$/.test(draft.qty.trim()) || qty < 1 || qty > line.quantity_prescribed) {
    return `Dispense a whole number from 1 to ${line.quantity_prescribed}, or mark it not available.`;
  }
  const mrp = toPaise(draft.mrp);
  if (mrp === null) return "Enter a valid MRP (up to 2 decimal places).";
  if (mrp === 0n) return "Enter the MRP: it must be more than zero.";
  if (BigInt(qty) * mrp > MAX_PAISE) return "This line's amount is too large.";
  if (qty < line.quantity_prescribed && isBlank(draft.remark)) return "A remark is required when dispensing less than prescribed.";
  return null;
}

/** Why the delivery charge can't be used, or null when it can. */
export const deliveryProblem = (input: string): string | null =>
  toPaise(input) === null ? "Enter a valid delivery charge (up to 2 decimal places)." : null;

/** One line in the publish request. A remark is only sent with a short dispense. */
export function linePricingBody(line: OrderLine, draft: LineDraft) {
  const qty = Number(draft.qty.trim());
  return {
    line_id: line.id,
    quantity_dispensed: qty,
    quantity_remark: qty < line.quantity_prescribed ? draft.remark.trim() || null : null,
    mrp_per_unit: draft.mrp.trim(),
  };
}

/** GST on one priced line as saved by the server (only one of CGST+SGST or IGST is non-zero). */
export const lineGst = (line: OrderLine): number =>
  Number(line.cgst_amount) + Number(line.sgst_amount) + Number(line.igst_amount);

export interface PricingPreview {
  /** Per available line: its amount and GST, or null while its inputs are invalid. */
  lines: Record<string, { taxable: bigint; gst: bigint } | null>;
  medicines: bigint;
  delivery: bigint;
  gst: bigint;
  total: bigint;
  /** Every available line and the delivery charge are valid, so `total` is what the patient pays. */
  complete: boolean;
}

/** The order totals for the typed values, as the server will compute them on publish. */
export function previewPricing(
  lines: OrderLine[],
  basis: PricingBasis,
  draftOf: (line: OrderLine) => LineDraft,
  deliveryInput: string,
): PricingPreview {
  const out: PricingPreview = { lines: {}, medicines: 0n, delivery: 0n, gst: 0n, total: 0n, complete: true };
  for (const line of lines) {
    if (!line.is_available) continue; // the server zeroes unavailable lines
    const draft = draftOf(line);
    const pct = basis.line_gst_pct[line.id];
    const mrp = toPaise(draft.mrp);
    const taxable = lineProblem(line, draft) === null && mrp !== null ? BigInt(Number(draft.qty.trim())) * mrp : null;
    const gst = taxable !== null && pct !== undefined ? gstPaise(taxable, pct, basis.intra_state) : null;
    if (taxable === null || gst === null) {
      out.lines[line.id] = null;
      out.complete = false;
      continue;
    }
    out.lines[line.id] = { taxable, gst };
    out.medicines += taxable;
    out.gst += gst;
  }
  const delivery = toPaise(deliveryInput);
  if (delivery === null) {
    out.complete = false;
  } else {
    out.delivery = delivery;
    // pricing_service only adds delivery GST when there is a delivery charge.
    const deliveryGst = delivery > 0n ? gstPaise(delivery, basis.delivery_gst_pct, basis.intra_state) : 0n;
    if (deliveryGst === null) out.complete = false;
    else out.gst += deliveryGst;
  }
  out.total = out.medicines + out.delivery + out.gst;
  if (out.total > MAX_PAISE) out.complete = false;
  return out;
}
